/**
 * Decide con qué visor se pinta un archivo: imagen (`ZoomImagen`, zoom solo sobre la foto) o
 * PDF/otro (`ZoomPdf`, visor nativo del navegador).
 *
 * Antes cada pantalla lo resolvía con `/\.(jpg|png…)$/` sobre la URL completa: un comprobante
 * con `?token=…`, `#page=…`, extensión en mayúsculas rara (`.HEIC`) o sin extensión caía al
 * `<iframe>`. Una imagen dentro de un iframe no tiene zoom propio: Ctrl+rueda o el pellizco del
 * trackpad agrandaban TODA la página y no se podía recorrer la foto (Relación de Pagos, 2026-10).
 *
 * Primero la extensión del path (sin query ni hash); si no la hay o no se reconoce, el
 * `Content-Type` que devuelva el servidor (ver `useTipoArchivoVisor`).
 */
export type TipoVisor = 'imagen' | 'pdf' | 'otro';

const EXT_IMAGEN = new Set(['jpg', 'jpeg', 'png', 'webp', 'gif', 'bmp', 'avif', 'svg', 'heic', 'heif', 'jfif', 'tif', 'tiff']);

/** Extensión del último segmento del path, en minúsculas; '' si no hay. */
export function extensionDeUrl(url?: string | null): string {
  if (!url) return '';
  const sinQuery = url.trim().split(/[?#]/)[0];
  let ultimo = sinQuery.slice(sinQuery.lastIndexOf('/') + 1);
  try {
    ultimo = decodeURIComponent(ultimo);
  } catch {
    /* path mal codificado: se usa tal cual */
  }
  const punto = ultimo.lastIndexOf('.');
  return punto > 0 ? ultimo.slice(punto + 1).toLowerCase() : '';
}

/**
 * Tipo por la extensión de la URL (o del nombre del archivo). Gana la primera fuente que diga
 * imagen o PDF; `'otro'` si alguna trae una extensión distinta; `null` si ninguna trae extensión.
 */
export function tipoVisorPorExtension(...fuentes: (string | null | undefined)[]): TipoVisor | null {
  let hayOtro = false;
  for (const f of fuentes) {
    const dataUri = /^data:([^;,]+)/i.exec(f ?? '');
    const tipo = dataUri ? tipoVisorPorContentType(dataUri[1]) : tipoPorExtension(extensionDeUrl(f));
    if (tipo === 'imagen' || tipo === 'pdf') return tipo;
    if (tipo === 'otro') hayOtro = true;
  }
  return hayOtro ? 'otro' : null;
}

function tipoPorExtension(ext: string): TipoVisor | null {
  if (!ext) return null;
  if (EXT_IMAGEN.has(ext)) return 'imagen';
  return ext === 'pdf' ? 'pdf' : 'otro';
}

/** Tipo por el `Content-Type` del servidor; `null` si no viene o es genérico. */
export function tipoVisorPorContentType(contentType?: string | null): TipoVisor | null {
  const ct = (contentType ?? '').split(';')[0].trim().toLowerCase();
  if (!ct || ct === 'application/octet-stream' || ct === 'binary/octet-stream') return null;
  if (ct.startsWith('image/')) return 'imagen';
  if (ct === 'application/pdf') return 'pdf';
  return 'otro';
}

/** Atajo síncrono (solo extensión) para quien no puede esperar al servidor. */
export function esImagenPorUrl(url?: string | null): boolean {
  return tipoVisorPorExtension(url) === 'imagen';
}

/**
 * Cómo preguntar el tipo cuando la extensión no alcanza: `HEAD` a http(s) (no baja el archivo);
 * `GET` a un `blob:` (vista previa local, ya está en memoria y no admite HEAD); nada en otro caso.
 */
export function metodoConsultaTipo(url?: string | null): 'HEAD' | 'GET' | null {
  const u = (url ?? '').trim();
  if (/^https?:\/\//i.test(u)) return 'HEAD';
  if (/^blob:/i.test(u)) return 'GET';
  return null;
}
